import {FunctionComponent, useMemo, useRef} from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {twMerge} from 'tailwind-merge';
import FormIcon from '@/assets/images/icon-form.svg?react';
import DocumentMenu from '@/components/document-menu';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {useNetworkStatus} from '@/contexts/network-status-context';
import useShowMenu from '@/hooks/use-show-menu';
import {UserProfile} from '@/repositories/beneva-api/endpoints/users';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {hasCompleteUserProfile} from '@/utils/user-profile';

interface ElementProps {
  profile: UserProfile | null;
}

const Menu: FunctionComponent<ElementProps> = ({profile}) => {
  const {t} = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {isOffline} = useNetworkStatus();

  const {isMenuVisible, onShowMenu, onCloseMenu} = useShowMenu();

  const canShowMenu = useMemo(() => {
    return hasCompleteUserProfile(profile);
  }, [profile]);

  const mergedClasses = clsx('group flex justify-start gap-2', isOffline && 'cursor-not-allowed opacity-20');

  if (!canShowMenu) return;

  return (
    <div data-testid="menu">
      <TrackedButton
        data-testid="menu-toggle"
        type="button"
        ref={buttonRef}
        className={twMerge(mergedClasses)}
        onClick={onShowMenu}
        aria-label={t('components.application-header.forms.accessibility-button')}
        trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_MY_REQUESTS}
        trackingAs={TrackingClickPrefix.CONTENT}
      >
        <FormIcon
          role="presentation"
          className="h-[26px] w-[22px] fill-violet transition-opacity duration-200 ease-in-out group-hover:opacity-90"
        />

        <span className="font-semibold text-charcoal transition-colors duration-200 ease-in-out group-hover:text-violet">
          {t('components.application-header.forms.button')}
        </span>
      </TrackedButton>

      <DocumentMenu isVisible={isMenuVisible} onClose={onCloseMenu} toggleRef={buttonRef} />
    </div>
  );
};

export default Menu;
